import { ActionFunctionArgs, LoaderFunctionArgs, redirect } from "react-router-dom";
import { saveAs } from "file-saver";

import { myAxios } from "../common/functions";
import { appendField } from "../common/FilterFrame/FilterField";
import { fields } from "./AssignmentFilterForm";
import { buildDefaultState } from "../common/FilterFrame/FilterModal";
import { extraFields } from "./Assignments";

export const ASSIGNMENT_EXPORT_COLUMNS = [
    { key: "workName", label: "Work" },
    { key: "projectName", label: "Project" },
    { key: "category", label: "Category" },
    { key: "subCategory", label: "Sub Category" },
    { key: "location", label: "Location" },
    { key: "startTime", label: "Start Time" },
    { key: "endTime", label: "End Time" },
    { key: "allDay", label: "All Day" },
    { key: "supervisorName", label: "Supervisor" },
    { key: "siteContactName", label: "Site Contact" },
    { key: "cadRequired", label: "CAD Required" },
    { key: "reportRequired", label: "Report Required" },
    { key: "waiveJSA", label: "Waive JSA" },
    { key: "leadName", label: "Lead" },
    { key: "description", label: "Description" },
    { key: "void", label: "Void" },
    { key: "voidReason", label: "Void Reason" },
    { key: "creatorName", label: "Creator" },
    { key: "createdAt", label: "Created At" },
    { key: "updaterName", label: "Updater" },
    { key: "updatedAt", label: "Updated At" },
];

export async function assignmentsLoader({ request }: LoaderFunctionArgs) {
    const url = new URL(request.url);
    let shouldRedirect = false;
    for (const [key] of url.searchParams) {
        if(![...Object.keys(buildDefaultState(fields)), ...extraFields].includes(key)){
            url.searchParams.delete(key);
            shouldRedirect = true;
        }
    }
    if (!url.searchParams.get("page")) {
        url.searchParams.set("page", "1");
        shouldRedirect = true;
    }
    if (!url.searchParams.get("limit")) {
        url.searchParams.set("limit", "10");
        shouldRedirect = true;
    }
    if (shouldRedirect) {
        throw redirect(url.pathname + "?" + url.searchParams.toString());
    }

    const formData = new FormData();
    formData.append("page", String(Number(url.searchParams.get("page"))));
    formData.append("limit", String(Number(url.searchParams.get("limit"))));
    for (const field of fields) {
        appendField(formData, url.searchParams, field);
    }
    if (url.searchParams.get("workId")) {
        formData.append("workId", String(url.searchParams.get("workId")));
    }

    const res = await myAxios({ fileName: "assignments/readAssignments.php", formData });
    if (!res) throw redirect("..");
    return res.data;
}

export async function assignmentLoader({ params }: LoaderFunctionArgs) {
    const { assignmentId } = params;
    if (!assignmentId) throw redirect("/Assignments");

    const formData = new FormData();
    formData.append("id", assignmentId);
    let res = await myAxios({ fileName: "assignments/readAssignment.php", formData });
    let data = res?.data;

    if (!res || !data) {
        res = await myAxios({ fileName: "assignments/readAssignments.php", formData });
        data = res?.data;
    }
    if (!res || !data) throw redirect("..");
    return data;
}

export async function deleteAssignmentAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => controller.abort());

    const formData = await request.formData();
    if (!formData.get("id")) {
        throw new Response("Missing id", { status: 400 });
    }

    const res = await myAxios({
        fileName: "assignments/deleteAssignment.php",
        formData,
        signal: controller.signal,
    });
    if (!res) throw redirect("..");
    throw redirect("../..");
}

export async function voidAssignmentAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => controller.abort());

    const formData = await request.formData();
    if (!formData.get("id")) {
        throw new Response("Missing id", { status: 400 });
    }

    await myAxios({
        fileName: "assignments/voidAssignment.php",
        formData,
        signal: controller.signal,
    });
    throw redirect("..");
}

export async function restoreAssignmentAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => controller.abort());

    const formData = await request.formData();
    if (!formData.get("id")) {
        throw new Response("Missing id", { status: 400 });
    }

    await myAxios({
        fileName: "assignments/restoreAssignment.php",
        formData,
        signal: controller.signal,
    });
    throw redirect("..");
}

export async function submitAssignmentAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => controller.abort());

    const formData = await request.formData();
    if (!formData.get("id")) {
        throw new Response("Missing id", { status: 400 });
    }

    const res = await myAxios({
        fileName: "assignments/submit.php",
        formData,
        signal: controller.signal,
    });
    if (!res) throw redirect("..");
    throw redirect("..");
}

export async function withdrawAssignmentAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => controller.abort());

    const formData = await request.formData();
    if (!formData.get("id")) {
        throw new Response("Missing id", { status: 400 });
    }

    const res = await myAxios({
        fileName: "assignments/withdraw.php",
        formData,
        signal: controller.signal,
    });
    if (!res) throw redirect("..");
    throw redirect("..");
}

export async function assignmentExportAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => controller.abort());

    const url = new URL(request.url);
    const formData = await request.formData();
    for (const field of fields) {
        appendField(formData, url.searchParams, field);
    }

    const res = await myAxios({
        fileName: "/assignments/exportAssignments.php",
        formData,
        responseType: "blob",
        signal: controller.signal,
    });
    if (!res) throw redirect(`..?${url.searchParams.toString()}`);

    const disposition = res.headers["content-disposition"];
    let filename = "assignments.xlsx";
    if (disposition) {
        const match = disposition.match(/filename="?(.+?)"?$/);
        if (match) filename = decodeURIComponent(match[1]);
    }

    saveAs(
        new Blob([res.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        filename
    );
    throw redirect(`..?${url.searchParams.toString()}`);
}

export const JobTagSubmit = async ({ request }: ActionFunctionArgs) => {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => controller.abort());
    const formData = await request.formData();
    if (!formData.get("assignmentId")) {
        throw new Response("Missing assignmentId", { status: 400 });
    }
    const res = await myAxios({
        fileName: "/assignments/jobTag.php",
        formData,
        signal: controller.signal,
    });
    if (!res) return;
    throw redirect("..");
};

export const JSASubmit = async ({ request }: ActionFunctionArgs) => {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => controller.abort());

    const formData = await request.formData();
    if (!formData.get("assignmentId")) {
        throw new Response("Missing assignmentId", { status: 400 });
    }

    const isMulti = Boolean(formData.get("formId"));
    const res = await myAxios({
        fileName: isMulti ? "/assignments/mutiJSA.php" : "/assignments/jsa.php",
        formData,
        signal: controller.signal,
    });
    if (!res) return;
    throw redirect("..");
};

export const JobTagLoader = async ({ params }: LoaderFunctionArgs) => {
    return null;
}
